import { downloadTravelReportPdf } from '@/lib/travel-report-export';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, ArrowUpDown, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePermission } from '@/components/guards/permission-guard';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { downloadXlsx, dateStamp } from '@/lib/download-xlsx';
import { showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';
import {
  selectClass,
  useTravelCariTypes,
  CURRENCIES,
  fmtMoney,
  useCariTypeLabel,
  useFiscalYear,
} from '../shared';

// Net balance per currency per cari, within ONE legacy fiscal year
// (each year is self-contained: opening voucher + that year's movements).
export function TravelCariBalancesPage() {
  const canExport = usePermission('ACCOUNTING', 'EXPORT', 'TRAVEL');
  const availableTypes = useTravelCariTypes();
  const { t } = useTranslation('accounting');
  const navigate = useNavigate();
  const typeLabel = useCariTypeLabel();
  const { years, fiscalYear, setFiscalYear } = useFiscalYear();

  const [debounced, setDebounced] = useSessionState<string>('travel:cari-balances:debounced', '');
  const [search, setSearch] = useState(debounced);
  const [cariType, setCariType] = useSessionState<string>('travel:cari-balances:cariType', '');
  const [currency, setCurrency] = useSessionState<string>('travel:cari-balances:currency', '');
  const [sortBy, setSortBy] = useSessionState<'name' | 'code' | 'balance'>(
    'travel:cari-balances:sortBy',
    'name'
  );
  const [sortDir, setSortDir] = useSessionState<'asc' | 'desc'>(
    'travel:cari-balances:sortDir',
    'asc'
  );
  const [exporting, setExporting] = useState(false);

  const utils = trpc.useUtils();

  const onSearch = (v: string) => {
    setSearch(v);
    setTimeout(() => setDebounced(v), 300);
  };

  const queryInput = fiscalYear
    ? {
        fiscalYear,
        search: debounced || undefined,
        cariType: (cariType || undefined) as (typeof availableTypes)[number] | undefined,
        currency: (currency || undefined) as (typeof CURRENCIES)[number] | undefined,
        sortBy,
        sortDir,
      }
    : undefined;

  const { data, isLoading } = trpc.travelCari.balances.useQuery(queryInput!, {
    enabled: !!queryInput,
  });

  const onExport = async (kind: 'xlsx' | 'pdf') => {
    if (!queryInput) return;
    setExporting(true);
    try {
      const res = await utils.travelCari.exportBalances.fetch(queryInput);
      if (kind === 'pdf')
        await downloadTravelReportPdf(
          res.rows,
          'Cari Bakiyeleri',
          [String(fiscalYear), search, cariType, currency].filter(Boolean),
          `travel-cari-bakiyeleri-${fiscalYear}-${dateStamp()}.pdf`
        );
      else
        downloadXlsx(
          res.rows,
          'Cari Bakiyeleri',
          `travel-cari-bakiyeleri-${fiscalYear}-${dateStamp()}.xlsx`
        );
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const cellFor = (row: NonNullable<typeof data>['items'][number], cur: string) => {
    const b = row.byCurrency[cur];
    if (!b) return null;
    return cur === 'TRY' ? b.tryNet : b.fxNet;
  };

  const items = data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('cari.balancesTitle', 'Cari Balances')}</h1>
          <p className="text-muted-foreground">
            {t('cari.balancesSubtitle', 'Net balance per currency within the selected fiscal year')}
            {data ? ` (${data.total})` : ''}
          </p>
        </div>
        {canExport && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onExport('pdf')}
              disabled={exporting || !items.length}
            >
              PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => onExport('xlsx')}
              disabled={exporting || !items.length}
            >
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Excel
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs">{t('cari.fiscalYear', 'Fiscal Year')}</Label>
          <select
            value={fiscalYear ?? ''}
            onChange={(e) => setFiscalYear(Number(e.target.value))}
            className={`${selectClass} block w-24`}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">{t('cari.search', 'Search')}</Label>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={t('cari.searchPlaceholder', 'Name or cari code...')}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 mt-1 h-8 w-64 border px-3 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cari.type', 'Type')}</Label>
          <select
            value={cariType}
            onChange={(e) => setCariType(e.target.value)}
            className={`${selectClass} block w-48`}
          >
            <option value="">{t('cari.allTypes', 'All types')}</option>
            {availableTypes.map((ct) => (
              <option key={ct} value={ct}>
                {ct} · {typeLabel(ct)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cari.currency', 'Currency')}</Label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className={`${selectClass} block w-28`}
          >
            <option value="">{t('cari.all', 'All')}</option>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (sortBy === 'balance') setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
            else {
              setSortBy('balance');
              setSortDir('desc');
            }
          }}
        >
          <ArrowUpDown className="mr-2 h-3.5 w-3.5" />
          {sortBy === 'balance'
            ? t('cari.sortBalance', 'Balance') + (sortDir === 'asc' ? ' ↑' : ' ↓')
            : t('cari.sortByBalance', 'Sort by balance')}
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="px-3 py-3 text-left font-medium">{t('cari.cari', 'Cari')}</th>
              <th className="px-3 py-3 text-left font-medium">{t('cari.type', 'Type')}</th>
              {CURRENCIES.map((c) => (
                <th key={c} className="px-3 py-3 text-right font-medium">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={2 + CURRENCIES.length} className="py-8 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                </td>
              </tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td
                  colSpan={2 + CURRENCIES.length}
                  className="text-muted-foreground py-8 text-center"
                >
                  {t('cari.noResults', 'No accounts found')}
                </td>
              </tr>
            )}
            {items.map((row) => (
              <tr
                key={row.cariCode}
                className="hover:bg-muted/30 cursor-pointer border-b last:border-0"
                onClick={() =>
                  navigate(`/travel/cari/${row.cariCode}/movements?year=${fiscalYear}`)
                }
              >
                <td className="px-3 py-2.5">
                  <span className="font-medium">{row.counterpartyName}</span>
                  <span className="text-muted-foreground ml-2 font-mono text-xs">
                    {row.cariCode}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <span className="bg-muted rounded px-1.5 py-0.5 text-xs">{row.cariType}</span>
                </td>
                {CURRENCIES.map((c) => {
                  const v = cellFor(row, c);
                  return (
                    <td
                      key={c}
                      className={`px-3 py-2.5 text-right tabular-nums ${v == null ? 'text-muted-foreground/40' : v >= 0 ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {v == null ? '—' : fmtMoney(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-muted-foreground text-xs">
        {t(
          'cari.legend',
          'Green = they owe us (debit) · Red = we owe them (credit). Click a row to open the ledger.'
        )}
      </p>
    </div>
  );
}
